import { Spin, App, Collapse, Image } from "antd";
import { useEffect, useState } from "react";
import Detail from "../../../components/Details/Detail";
import {
  ERROR_MSGS,
  LABEL_TXT,
  ENT_STATUS,
  TITLE_TXT,
  FORM_MODAL_WIDTHS,
} from "../../../constants/constants";
import { useSelector, useDispatch } from "react-redux";
import { getDropdownData } from "../../../redux/slices/EnqMngtSlice";
import {
  getDesignations,
  updateErrorStatus,
} from "../../../redux/slices/enterpriseSlice";
import {
  capitalizeWords,
  descriptionRules,
  emailRules,
  formatCurrency,
  nameRules,
  phoneNumberRules,
  websiteRules,
  financialSummaryRules,
  CommentsRules,
} from "../../../helpers/helpers";
import { PERM_CODE } from "../../../constants/PermissionCodes";
import ArrowDownIcon from "../../../assets/icons/arrow-down-icon.svg";
import dayjs from "dayjs";
import CustomModal from "../../../components/CustomModal/CustomModal";
import ComAddressForm from "../../../components/CustomForms/ComAddressForm";
import DetailHeaderWithEdit from "../../../components/Details/DetailHeaderWithEdit";

const EnterpriseDetails = ({ entDetailedData, entId, status }) => {
  const { notification } = App.useApp();
  const dispatch = useDispatch();
  const { dropdownData } = useSelector((state) => state?.enqMngt);
  const { loading, designationsData, editError } = useSelector(
    (state) => state?.enterprise,
  );
  const { user } = useSelector((state) => state.auth);
  const [activeKey, setActiveKey] = useState(["1"]);
  const [modal, setModal] = useState({
    type: null, // "editComAdd"
    data: null, // hold address info
  });

  const userPrivileges = user?.privilege?.map((p) => p.privilegeCode) || [];
  const editAccess =
    (userPrivileges.includes(PERM_CODE.EPEDT) &&
      status === ENT_STATUS.PENDING.ID) ||
    (userPrivileges.includes(PERM_CODE.EVEDT) &&
      status === ENT_STATUS.ACTIVE.ID) ||
    (userPrivileges.includes(PERM_CODE.EDEDT) &&
      status === ENT_STATUS.DEACTIVATED.ID) ||
    (userPrivileges.includes(PERM_CODE.EBEDT) &&
      status === ENT_STATUS.BLACKLISTED.ID);

  const panelStyle = {
    marginBottom: 24,
    borderRadius: 2,
    borderBottom: "none",
  };

  useEffect(() => {
    dispatch(getDropdownData());
    dispatch(getDesignations());
    return () => {
      dispatch(updateErrorStatus());
    };
  }, [entId, dispatch]);

  useEffect(() => {
    if (editError) {
      notification.error({
        message: "Error",
        description: editError,
        placement: "topRight",
      });
    }
  }, [editError, dispatch, notification]);

  const primaryAddress = (
    <>
      <Detail
        label={LABEL_TXT.ADD1}
        value={entDetailedData?.primaryAddress?.address1}
        isVerified={entDetailedData?.kycDetails?.kycGstinValue?.isVerified}
      />
      <Detail
        label={LABEL_TXT.ADD2}
        value={entDetailedData?.primaryAddress?.address2}
      />
      <Detail
        label={LABEL_TXT.CITY}
        value={entDetailedData?.primaryAddress?.city}
      />
      <Detail
        label={LABEL_TXT.PIN}
        value={entDetailedData?.primaryAddress?.pinCode}
      />
      <Detail
        label={LABEL_TXT.STATE}
        value={entDetailedData?.primaryAddress?.state}
      />
    </>
  );

  const comAddress = (
    <>
      <Detail
        label={LABEL_TXT.ADD1}
        value={entDetailedData?.communicationAddress?.address1}
      />
      <Detail
        label={LABEL_TXT.ADD2}
        value={entDetailedData?.communicationAddress?.address2}
      />
      <Detail
        label={LABEL_TXT.CITY}
        value={entDetailedData?.communicationAddress?.city}
      />
      <Detail
        label={LABEL_TXT.PIN}
        value={entDetailedData?.communicationAddress?.pinCode}
      />
      <Detail
        label={LABEL_TXT.STATE}
        value={entDetailedData?.communicationAddress?.state}
      />
    </>
  );

  const handleComAddEdit = () => {
    const data = {
      entId: entDetailedData?.entId,
      billingAddId: entDetailedData?.communicationAddress?.addressId,
      billingAddLine1: entDetailedData?.communicationAddress?.address1,
      billingAddLine2: entDetailedData?.communicationAddress?.address2,
      billingCityName: entDetailedData?.communicationAddress?.city,
      billingStateName: entDetailedData?.communicationAddress?.state,
      billingZipCode: entDetailedData?.communicationAddress?.pinCode,
    };
    setModal({ type: "editComAdd", data });
    setActiveKey((prev) => [...prev, "2"]);
  };

  const closeModal = () => setModal({ type: null, data: null });

  const collapseItems = [
    {
      key: "1",
      label: TITLE_TXT.PRIM_ADD,
      children: primaryAddress,
      style: panelStyle,
    },
    {
      key: "2",
      label: editAccess ? (
        <DetailHeaderWithEdit
          title={TITLE_TXT.COMM_ADD}
          handleEdit={handleComAddEdit}
        />
      ) : (
        TITLE_TXT.COMM_ADD
      ),
      children: comAddress,
      style: panelStyle,
    },
  ];

  return (
    <div className="details-card-container">
      <Spin spinning={loading}>
        <Detail
          label={LABEL_TXT.ENT_NAME}
          value={entDetailedData?.entName}
          isVerified={entDetailedData?.kycDetails?.kycPanValue?.isVerified}
        />
        <Detail
          label={LABEL_TXT.IND_TYPE}
          value={entDetailedData?.industryTypeId}
          displayValue={entDetailedData?.industryTypeName}
          name="industryTypeId"
          placeholder="Select Industry Type"
          options={dropdownData?.industryTypeList?.map((industry) => ({
            value: industry.industryTypeId,
            label: capitalizeWords(industry.industryTypeName),
          }))}
          rules={[{ required: true, message: ERROR_MSGS.SEL_IND_TYPE }]}
          isEditable={editAccess}
          entId={entDetailedData?.entId}
        />
        <Detail
          label={LABEL_TXT.POC_NAME}
          value={entDetailedData?.poc?.name}
          name="pocName"
          placeholder="Enter POC Name"
          rules={nameRules("POC")}
          isEditable={editAccess}
          isInput={true}
          entId={entDetailedData?.entId}
        />
        <Detail
          label={LABEL_TXT.POC_DESIG}
          value={entDetailedData?.poc?.designationId}
          displayValue={entDetailedData?.poc?.designationName}
          placeholder="Select Designation"
          name="designationId"
          options={designationsData?.map((desig) => ({
            value: desig.designationId,
            label: capitalizeWords(desig.designationName),
          }))}
          rules={[{ required: true, message: ERROR_MSGS.DESIGNATION_ERR_MEG }]}
          isEditable={editAccess}
          entId={entDetailedData?.entId}
        />
        <Detail
          label={LABEL_TXT.POC_PHONE}
          value={entDetailedData?.poc?.phoneNumber}
          name="phoneNumber"
          placeholder="Enter Phone Number"
          rules={phoneNumberRules("POC")}
          isEditable={editAccess}
          isInput={true}
          entId={entDetailedData?.entId}
        />
        <Detail
          label={LABEL_TXT.POC_EMAIL}
          value={entDetailedData?.poc?.email}
          name="Email"
          placeholder="Enter Email"
          rules={emailRules("POC")}
          isEditable={editAccess}
          isInput={true}
          entId={entDetailedData?.entId}
        />
        <Detail
          label={LABEL_TXT.PAN}
          value={entDetailedData?.pan}
          isVerified={entDetailedData?.kycDetails?.kycPanValue?.isVerified}
        />
        <Detail
          label={LABEL_TXT.GSTIN}
          value={entDetailedData?.gstin}
          isVerified={entDetailedData?.kycDetails?.kycGstinValue?.isVerified}
        />
        <Detail
          label={LABEL_TXT.ENT_WEB}
          value={entDetailedData?.website?.replace(/^https?:\/\//i, "")}
          name="website"
          placeholder="Enter Enterprise Website"
          rules={websiteRules}
          isEditable={editAccess}
          isInput={true}
          entId={entDetailedData?.entId}
        />
        <Detail
          label={LABEL_TXT.INC_DATE}
          value={
            entDetailedData?.incorporationDate
              ? dayjs(entDetailedData.incorporationDate)
              : null
          }
          displayValue={
            entDetailedData?.incorporationDate
              ? dayjs(entDetailedData.incorporationDate).format("DD/MM/YYYY")
              : ""
          }
          name="incorporationDate"
          placeholder="Select Incorporation Date"
          rules={[{ required: true, message: ERROR_MSGS.ENTER_INC_DATE }]}
          isEditable={editAccess}
          isDate={true}
          entId={entDetailedData?.entId}
        />

        <Detail
          label={LABEL_TXT.EXP_AMT}
          value={formatCurrency(entDetailedData?.creditExposure)}
        />

        <Detail
          label={LABEL_TXT.ENT_DESC}
          value={entDetailedData?.description}
          name="description"
          placeholder="Enter Enterprise Description"
          rules={descriptionRules("Enterprise")}
          isEditable={editAccess}
          isInput={true}
          isTextArea={true}
          entId={entDetailedData?.entId}
        />
        <Detail
          label={LABEL_TXT.FIN_SUM}
          value={entDetailedData?.financialSummary}
          name="financialSummary"
          placeholder="Enter Financial Summary"
          rules={financialSummaryRules}
          isEditable={editAccess}
          isInput={true}
          isTextArea={true}
          entId={entDetailedData?.entId}
        />
        <Detail
          label={LABEL_TXT.COMMENTS}
          value={entDetailedData?.comments}
          name="comments"
          placeholder="Enter Comments"
          rules={CommentsRules}
          isEditable={editAccess}
          isInput={true}
          isTextArea={true}
          entId={entDetailedData?.entId}
        />
        <Collapse
          bordered={false}
          activeKey={activeKey}
          onChange={(key) => setActiveKey(key)}
          expandIcon={({ isActive }) => (
            <Image
              src={ArrowDownIcon}
              alt="Arrow"
              preview={false}
              style={{
                transform: isActive ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s ease",
              }}
            />
          )}
          expandIconPosition="end"
          className="detail-collapse"
          items={collapseItems}
        />
        {modal.type && (
          <CustomModal
            title={TITLE_TXT.EDIT_COM_ADD}
            open
            centered
            width={FORM_MODAL_WIDTHS}
            content={
              <ComAddressForm
                data={modal.data}
                onClose={closeModal}
                isEnt={true}
              />
            }
          />
        )}
      </Spin>
    </div>
  );
};

export default EnterpriseDetails;
